import { motion } from "framer-motion";

/**
 * Editorial photo collage — anchor photo (SXSW solo portrait) with layered
 * supporting photos, slight rotations, hard borders, and tape/sticker accents.
 * On hover each photo straightens itself and lifts forward.
 */

const hoverProps = {
  whileHover: { rotate: 0, scale: 1.03, zIndex: 40 },
  transition: { type: "spring", stiffness: 220, damping: 18 },
};

export default function Collage() {
  return (
    <div
      className="relative hidden md:block"
      data-testid="hero-collage"
      aria-label="JP Kuster at brand activations, events, and on location"
    >
      <div className="relative w-full pb-[130%]">
        {/* Anchor: SXSW solo portrait */}
        <motion.figure
          initial={{ opacity: 0, y: 30, rotate: -2 }}
          animate={{ opacity: 1, y: 0, rotate: -2 }}
          whileHover={{ rotate: 0, scale: 1.03, zIndex: 40 }}
          transition={{ duration: 0.9, delay: 0.55, type: "spring", stiffness: 220, damping: 18 }}
          className="absolute top-0 left-[6%] w-[62%] aspect-[3/4] hard-border bg-[#111] shadow-[6px_6px_0_#111] cursor-pointer"
          style={{ willChange: "transform" }}
        >
          <img
            src="/photos/sxsw.jpeg"
            alt="JP Kuster at SXSW Interactive 2023"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <figcaption className="absolute -bottom-3 left-3 sticker" style={{ transform: "rotate(-3deg)" }}>
            SXSW ’23
          </figcaption>
        </motion.figure>

        {/* Top right: Jack Harlow / Revolution */}
        <motion.figure
          initial={{ opacity: 0, y: 20, rotate: 4 }}
          animate={{ opacity: 1, y: 0, rotate: 4 }}
          {...hoverProps}
          transition={{ duration: 0.9, delay: 0.75, type: "spring", stiffness: 220, damping: 18 }}
          className="absolute top-[8%] right-0 w-[44%] aspect-[4/5] hard-border bg-[#111] shadow-[5px_5px_0_#D83127] cursor-pointer"
          style={{ willChange: "transform" }}
        >
          <img
            src="/photos/jack_harlow.jpeg"
            alt="JP Kuster at Revolution Cannabis activation with Jack Harlow"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <figcaption className="absolute -top-3 -left-3 sticker" style={{ transform: "rotate(6deg)" }}>
            Revolution
          </figcaption>
        </motion.figure>

        {/* Bottom left: Choose Chicago × Sentrock */}
        <motion.figure
          initial={{ opacity: 0, y: 20, rotate: 3 }}
          animate={{ opacity: 1, y: 0, rotate: 3 }}
          {...hoverProps}
          transition={{ duration: 0.9, delay: 0.9, type: "spring", stiffness: 220, damping: 18 }}
          className="absolute bottom-[2%] left-0 w-[40%] aspect-[4/5] hard-border bg-[#111] shadow-[4px_4px_0_#2E4F36] z-10 cursor-pointer"
          style={{ willChange: "transform" }}
        >
          <img
            src="/photos/choose_chicago.jpeg"
            alt="JP Kuster at Choose Chicago × Sentrock activation"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <figcaption
            className="absolute -bottom-2 right-3 font-mono-s text-[9px] tracking-[0.25em] uppercase bg-[#F2EFE9] hard-border px-2 py-1"
            style={{ transform: "rotate(-4deg)" }}
          >
            Art Basel · CHI
          </figcaption>
        </motion.figure>

        {/* Bottom right: JP Graziano popup */}
        <motion.figure
          initial={{ opacity: 0, y: 20, rotate: -5 }}
          animate={{ opacity: 1, y: 0, rotate: -5 }}
          {...hoverProps}
          transition={{ duration: 0.9, delay: 1.05, type: "spring", stiffness: 220, damping: 18 }}
          className="absolute bottom-[10%] right-[8%] w-[38%] aspect-[4/5] hard-border bg-[#111] shadow-[4px_4px_0_#111] z-20 cursor-pointer"
          style={{ willChange: "transform" }}
        >
          <img
            src="/photos/graziano_popup.jpeg"
            alt="JP Kuster outside JP Graziano popup"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </motion.figure>

        {/* Sentrock wall — small accent, mid-right */}
        <motion.figure
          initial={{ opacity: 0, y: -10, rotate: -8 }}
          animate={{ opacity: 1, y: 0, rotate: -8 }}
          {...hoverProps}
          transition={{ duration: 0.9, delay: 1.2, type: "spring", stiffness: 220, damping: 18 }}
          className="absolute top-[48%] left-[50%] w-[30%] aspect-square hard-border bg-[#111] shadow-[3px_3px_0_#D83127] z-[5] cursor-pointer"
          style={{ willChange: "transform" }}
        >
          <img
            src="/photos/sentrock_wall.jpeg"
            alt="JP Kuster at Sentrock wall installation"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </motion.figure>

        {/* Paper tape accents */}
        <div
          className="absolute top-[42%] left-[18%] w-20 h-6 bg-[#E8E4DB] opacity-85 z-30 hard-border pointer-events-none"
          style={{ transform: "rotate(-25deg)" }}
          aria-hidden
        />
        <div
          className="absolute top-[6%] right-[22%] w-16 h-6 bg-[#E8E4DB] opacity-85 z-30 hard-border pointer-events-none"
          style={{ transform: "rotate(15deg)" }}
          aria-hidden
        />
      </div>
    </div>
  );
}
